"use client";

import { Calendar, ChevronRight, User } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="h-12 w-3/4 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-[400px] w-full bg-muted/40 animate-pulse rounded-md" />
        <div className="space-y-3">
          <div className="h-4 w-full bg-muted/40 animate-pulse rounded-md" />
          <div className="h-4 w-full bg-muted/40 animate-pulse rounded-md" />
          <div className="h-4 w-5/6 bg-muted/40 animate-pulse rounded-md" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Unable to load this travel guide.</p>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">No travel content available.</p>
      </div>
    );
  }

  const post = posts[0];
  const formattedDate = post.date
    ? new Date(post.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const authorAvatar =
    post.author?.avatar?.["96"] ||
    post.author?.avatar?.["48"] ||
    post.author?.avatar?.["24"] ||
    "";

  return (
    <article className="max-w-4xl mx-auto">
      {}
      <nav className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground mb-10">
        <Link to="/HomePage" className="hover:text-primary transition-colors">
          Home
        </Link>
        <ChevronRight className="h-3 w-3" />
        <span className="text-foreground">Travel Blog</span>
      </nav>

      {}
      {post.categories && post.categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          {post.categories.map((cat, i) => (
            <span
              key={cat.id}
              data-index={i}
              className="text-xs uppercase tracking-[0.2em] text-primary border border-primary/30 px-3 py-1 rounded-lg"
            >
              {cat.name}
            </span>
          ))}
        </div>
      )}

      {}
      <h1 className="font-default text-4xl md:text-5xl lg:text-6xl font-light leading-tight tracking-tight text-foreground mb-8">
        {post.title}
      </h1>

      {}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-muted-foreground mb-12">
        {post.author?.name && (
          <div className="flex items-center gap-2">
            <User className="h-4 w-4" />
            <span>By Marga Travel</span>
          </div>
        )}
        {formattedDate && (
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4" />
            <time>{formattedDate}</time>
          </div>
        )}
      </div>

      <Separator className="bg-border/50 mb-12" />

      {}
      {post.featuredImage && (
        <figure className="mb-12 -mx-4 md:mx-0">
          <img
            src={post.featuredImage}
            alt={post.title}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover rounded-lg shadow-lg"
          />
        </figure>
      )}

      {}
      <div
        className="prose prose-invert max-w-none font-default text-base md:text-lg leading-relaxed text-foreground/90 [&>p]:mb-6 [&>h2]:font-default [&>h2]:text-2xl md:[&>h2]:text-3xl [&>h2]:font-light [&>h2]:mt-12 [&>h2]:mb-6 [&>h2]:text-foreground [&>h3]:font-default [&>h3]:text-xl md:[&>h3]:text-2xl [&>h3]:font-light [&>h3]:mt-10 [&>h3]:mb-4 [&>h3]:text-foreground [&>a]:text-primary [&>a]:underline [&>blockquote]:border-l-2 [&>blockquote]:border-primary [&>blockquote]:pl-6 [&>blockquote]:italic [&>blockquote]:my-8 [&>blockquote]:text-muted-foreground [&>ul]:list-disc [&>ul]:pl-6 [&>ul]:mb-6 [&>ol]:list-decimal [&>ol]:pl-6 [&>ol]:mb-6 [&>li]:mb-2 [&>img]:rounded-lg [&>img]:my-8"
        dangerouslySetInnerHTML={{ __html: post.content || "" }}
      />

      {}
      {post.tags && post.tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-border/50">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-muted-foreground mr-2">
              Travel Tags:
            </span>
            {post.tags.map((tag, i) => (
              <span
                key={tag.id}
                data-index={i}
                className="text-xs text-foreground/80 bg-accent/50 px-3 py-1 rounded-lg"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8">
        <p className="text-sm text-muted-foreground italic">Published by Marga Travel</p>
      </div>
    </article>
  );
}

function RelatedPostsContent() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {[...Array(3)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-4">
            <div className="aspect-[4/3] bg-muted/40 animate-pulse rounded-lg" />
            <div className="h-5 w-3/4 bg-muted/40 animate-pulse rounded-lg" />
            <div className="h-4 w-full bg-muted/40 animate-pulse rounded-lg" />
          </div>
        ))}
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <p className="text-muted-foreground text-center">
        No related travel stories at the moment.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {posts.slice(0, 3).map((post, i) => {
        const date = post.date
          ? new Date(post.date).toLocaleDateString("en-US", {
              year: "numeric",
              month: "short",
              day: "numeric",
            })
          : "";
        return (
          <Link
            key={post.id}
            data-index={i}
            to={post.link || "#"}
            className="group block"
          >
            <Card className="bg-card border-border h-full overflow-hidden transition-all hover:shadow-lg hover:border-primary/30">
              {post.featuredImage && (
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
              )}
              <CardContent className="p-6">
                {date && (
                  <p className="text-xs uppercase tracking-widest text-primary mb-3">
                    {date}
                  </p>
                )}
                <h3 className="font-default text-xl font-light text-foreground leading-snug mb-3 group-hover:text-primary transition-colors">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <div
                    className="text-sm text-muted-foreground font-sans line-clamp-2 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  />
                )}
                <div className="mt-4 flex items-center gap-2 text-sm text-primary font-default">
                  <span>Read more</span>
                  <ChevronRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </div>
              </CardContent>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}

export default function TravelPost() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  const relatedQuery = new WP_Query({
    post_type: "post",
    per_page: 3,
  });

  return (
    <section data-section-id="3150"
      id="single-post-template-section"
      className="bg-background text-foreground py-20 md:py-28 px-4 md:px-8"
    >
      <div className="container mx-auto">
        <PostsProvider wp_query={wp_query}>
          <SinglePostContent />
        </PostsProvider>

        {}
        <div className="max-w-6xl mx-auto mt-24 md:mt-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary mb-3">
                Explore More
              </p>
              <h2 className="font-default text-3xl md:text-4xl font-light text-foreground">
                Related Travel Stories
              </h2>
            </div>
            <Link to="/HomePage">
              <Button
                variant="outline"
                className="border-border text-foreground hover:bg-accent hover:text-accent-foreground rounded-lg"
              >
                View All
                <ChevronRight className="h-4 w-4" />
              </Button>
            </Link>
          </div>

          <Separator className="bg-border/50 mb-12" />

          <PostsProvider wp_query={{ post_type: "post", per_page: 3 }}>
            <RelatedPostsContent />
          </PostsProvider>
        </div>
      </div>
    </section>
  );
}